/**
 * Retention & revenue-intelligence section (Analytics spec §3.1 MRR/movement,
 * §3.2 churn/tenure/freeze, §3.4 at-risk/revenue-at-risk/ghost, cohort). All read
 * from the 0147 views; event-derived series (MRR movement, churn) accumulate
 * going forward from membership_events, so they show a clear empty state until
 * transitions occur. Owner/manager (route-guarded). Backend-only data, surfaced
 * read-only here.
 */
import {
  getCohortRetention,
  getFreezeAnalytics,
  getGhostMembers,
  getMembersAtRisk,
  getMembershipChurn,
  getMemberTenure,
  getMrrMovement,
  getMrrSummary,
} from '@gym/api-client';
import { useQuery } from '@tanstack/react-query';
import { AlertTriangle, Ghost, Snowflake, TrendingUp, Users } from 'lucide-react';
import { type ReactNode, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { RankedBarChart, TrendChart } from '../../components/charts/index.js';
import { ExpandableCard } from '../../components/ui/ExpandableCard.js';
import { EmptyState } from '../../components/ui/Feedback.js';
import { KpiCard } from '../../components/ui/KpiCard.js';
import { ChartCard } from '../../components/ui/Layout.js';
import { Money, useFormatters, useLocale } from '../../components/ui/Money.js';
import { Chip } from '../../components/ui/StatusPill.js';
import { Table, TableWrap, TD, TH, THead, TR } from '../../components/ui/Table.js';
import { useAuth } from '../../lib/auth.js';
import { supabase } from '../../lib/supabase.js';

export function RetentionAnalytics(): ReactNode {
  const { t } = useTranslation();
  const fmt = useFormatters();
  const locale = useLocale();
  const { claims } = useAuth();
  const tenantId = claims?.tenant_id ?? '';
  const on = { enabled: !!tenantId };

  const mrr = useQuery({
    queryKey: ['an_mrr', tenantId],
    queryFn: () => getMrrSummary(supabase, tenantId),
    ...on,
  });
  const movement = useQuery({
    queryKey: ['an_mrr_mv', tenantId],
    queryFn: () => getMrrMovement(supabase, tenantId),
    ...on,
  });
  const churn = useQuery({
    queryKey: ['an_churn', tenantId],
    queryFn: () => getMembershipChurn(supabase, tenantId),
    ...on,
  });
  const cohort = useQuery({
    queryKey: ['an_cohort', tenantId],
    queryFn: () => getCohortRetention(supabase, tenantId),
    ...on,
  });
  const tenure = useQuery({
    queryKey: ['an_tenure', tenantId],
    queryFn: () => getMemberTenure(supabase, tenantId),
    ...on,
  });
  const freeze = useQuery({
    queryKey: ['an_freeze', tenantId],
    queryFn: () => getFreezeAnalytics(supabase, tenantId),
    ...on,
  });
  const atRisk = useQuery({
    queryKey: ['an_atrisk', tenantId],
    queryFn: () => getMembersAtRisk(supabase, tenantId),
    ...on,
  });
  const ghost = useQuery({
    queryKey: ['an_ghost', tenantId],
    queryFn: () => getGhostMembers(supabase, tenantId),
    ...on,
  });

  // ── Derived ────────────────────────────────────────────────────────────────
  const latestMovement = useMemo(() => (movement.data ?? []).at(-1) ?? null, [movement.data]);
  const movementBars = useMemo(() => {
    if (!latestMovement) return [];
    return [
      {
        label: t('analytics.intel.newMrr'),
        value: Math.round((latestMovement.new_mrr ?? 0) / 100),
        color: '#22C55E',
      },
      {
        label: t('analytics.intel.expansion'),
        value: Math.round((latestMovement.expansion_mrr ?? 0) / 100),
        color: '#38BDF8',
      },
      {
        label: t('analytics.intel.contraction'),
        value: -Math.round((latestMovement.contraction_mrr ?? 0) / 100),
        color: '#F59E0B',
      },
      {
        label: t('analytics.intel.churned'),
        value: -Math.round((latestMovement.churned_mrr ?? 0) / 100),
        color: '#EF4444',
      },
    ];
  }, [latestMovement, t]);

  const churnTrend = useMemo(
    () =>
      (churn.data ?? []).map((r) => ({
        label: fmt.date(r.month_bucket, { month: 'short' }),
        [t('analytics.intel.netGrowth')]: r.net_growth,
        [t('analytics.intel.churnRate')]: r.churn_rate_pct ?? 0,
      })),
    [churn.data, fmt, t],
  );

  const avgTenureMonths = useMemo(() => {
    const rows = tenure.data ?? [];
    if (rows.length === 0) return null;
    const avgDays = rows.reduce((s, r) => s + (r.tenure_days ?? 0), 0) / rows.length;
    return Math.round((avgDays / 30) * 10) / 10;
  }, [tenure.data]);

  const revenueAtRisk = useMemo(
    () => (atRisk.data ?? []).reduce((s, r) => s + (r.mrr_at_risk ?? 0), 0),
    [atRisk.data],
  );

  return (
    <div className="grid gap-5">
      {/* ── MRR / ARPM / revenue-at-risk headline ──────────────────────────── */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <KpiCard
          label={t('analytics.intel.mrr')}
          value={<Money minor={mrr.data?.mrr ?? 0} className="text-3xl" />}
          icon={TrendingUp}
          tone="primary"
          loading={mrr.isLoading}
          hint={t('analytics.intel.mrrHint')}
        />
        <KpiCard
          label={t('analytics.intel.arpm')}
          value={<Money minor={mrr.data?.arpm ?? 0} className="text-3xl" />}
          icon={Users}
          tone="info"
          loading={mrr.isLoading}
          hint={t('analytics.intel.arpmHint')}
        />
        <KpiCard
          label={t('analytics.intel.atRiskCount')}
          value={fmt.number(atRisk.data?.length ?? 0)}
          icon={AlertTriangle}
          tone="warning"
          loading={atRisk.isLoading}
          hint={t('analytics.intel.atRiskHint')}
        />
        <KpiCard
          label={t('analytics.intel.revenueAtRisk')}
          value={<Money minor={revenueAtRisk} className="text-3xl" />}
          icon={AlertTriangle}
          tone="danger"
          loading={atRisk.isLoading}
        />
      </div>

      {/* ── MRR movement + churn/growth trend ──────────────────────────────── */}
      <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
        <ChartCard
          title={t('analytics.intel.mrrMovement')}
          subtitle={t('analytics.intel.latestMonth')}
          loading={movement.isLoading}
          empty={movementBars.length === 0 ? t('analytics.intel.accruesForward') : undefined}
        >
          <RankedBarChart
            data={movementBars}
            formatValue={(v) => new Intl.NumberFormat(locale).format(v)}
          />
        </ChartCard>
        <ChartCard
          title={t('analytics.intel.churnGrowth')}
          subtitle={t('analytics.intel.perMonth')}
          loading={churn.isLoading}
          empty={churnTrend.length === 0 ? t('analytics.intel.accruesForward') : undefined}
        >
          <TrendChart
            data={churnTrend}
            series={[
              {
                key: t('analytics.intel.netGrowth'),
                name: t('analytics.intel.netGrowth'),
                color: '#22C55E',
              },
              {
                key: t('analytics.intel.churnRate'),
                name: t('analytics.intel.churnRate'),
                color: '#EF4444',
                area: false,
              },
            ]}
          />
        </ChartCard>
      </div>

      {/* ── tenure + freeze + ghost mini-KPIs ──────────────────────────────── */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <KpiCard
          label={t('analytics.intel.avgTenure')}
          value={
            avgTenureMonths != null ? t('analytics.intel.monthsValue', { n: avgTenureMonths }) : '—'
          }
          icon={Users}
          loading={tenure.isLoading}
        />
        <KpiCard
          label={t('analytics.intel.freezeReturned')}
          value={fmt.number(freeze.data?.returned_active ?? 0)}
          icon={Snowflake}
          tone="info"
          loading={freeze.isLoading}
          hint={t('analytics.intel.freezeExited', { n: fmt.number(freeze.data?.exited ?? 0) })}
        />
        <KpiCard
          label={t('analytics.intel.ghostMembers')}
          value={fmt.number(ghost.data?.length ?? 0)}
          icon={Ghost}
          tone="warning"
          loading={ghost.isLoading}
          hint={t('analytics.intel.ghostHint')}
        />
        <KpiCard
          label={t('analytics.intel.reactivations')}
          value={fmt.number((churn.data ?? []).reduce((s, r) => s + (r.reactivations ?? 0), 0))}
          icon={TrendingUp}
          tone="success"
          loading={churn.isLoading}
        />
      </div>

      {/* ── Members at risk list ───────────────────────────────────────────── */}
      <ExpandableCard
        title={t('analytics.intel.membersAtRisk')}
        subtitle={t('analytics.intel.atRiskRule')}
        loading={atRisk.isLoading}
        empty={(atRisk.data ?? []).length === 0 ? t('analytics.intel.noneAtRisk') : undefined}
      >
        <TableWrap>
          <Table>
            <THead>
              <tr>
                <TH>{t('members.fields.firstName')}</TH>
                <TH>{t('members.fields.phone')}</TH>
                <TH numeric>{t('analytics.intel.daysSinceVisit')}</TH>
                <TH numeric>{t('analytics.intel.visits7d')}</TH>
                <TH numeric>{t('analytics.intel.mrrAtRisk')}</TH>
              </tr>
            </THead>
            <tbody>
              {(atRisk.data ?? []).slice(0, 50).map((r) => (
                <TR key={r.member_id}>
                  <TD className="font-medium">
                    {r.first_name} {r.last_name}
                  </TD>
                  <TD className="text-muted">{r.phone ?? '—'}</TD>
                  <TD numeric>
                    <Chip
                      tone={
                        r.days_since_last_visit == null || r.days_since_last_visit > 30
                          ? 'danger'
                          : 'warning'
                      }
                    >
                      {r.days_since_last_visit == null
                        ? t('analytics.intel.never')
                        : r.days_since_last_visit}
                    </Chip>
                  </TD>
                  <TD numeric className="text-muted">
                    {r.visits_last_7_days}
                  </TD>
                  <TD numeric>{r.mrr_at_risk != null ? <Money minor={r.mrr_at_risk} /> : '—'}</TD>
                </TR>
              ))}
            </tbody>
          </Table>
        </TableWrap>
      </ExpandableCard>

      {/* ── Cohort retention ───────────────────────────────────────────────── */}
      <ExpandableCard
        title={t('analytics.intel.cohortRetention')}
        subtitle={t('analytics.intel.cohortSubtitle')}
        loading={cohort.isLoading}
        empty={(cohort.data ?? []).length === 0 ? t('common.noData') : undefined}
      >
        <TableWrap>
          <Table>
            <THead>
              <tr>
                <TH>{t('analytics.intel.cohortMonth')}</TH>
                <TH numeric>{t('analytics.intel.cohortSize')}</TH>
                <TH numeric>{t('analytics.intel.stillActive')}</TH>
                <TH numeric>{t('analytics.intel.retentionPct')}</TH>
              </tr>
            </THead>
            <tbody>
              {(cohort.data ?? []).map((r) => (
                <TR key={r.cohort_month}>
                  <TD className="font-medium">
                    {fmt.date(r.cohort_month, { month: 'short', year: 'numeric' })}
                  </TD>
                  <TD numeric className="text-muted">
                    {r.cohort_size}
                  </TD>
                  <TD numeric className="text-muted">
                    {r.retained_active}
                  </TD>
                  <TD numeric>
                    <Chip
                      tone={
                        (r.retention_pct ?? 0) >= 50
                          ? 'success'
                          : (r.retention_pct ?? 0) >= 25
                            ? 'warning'
                            : 'danger'
                      }
                    >
                      {r.retention_pct ?? 0}%
                    </Chip>
                  </TD>
                </TR>
              ))}
            </tbody>
          </Table>
        </TableWrap>
      </ExpandableCard>

      {(atRisk.data ?? []).length === 0 &&
      (cohort.data ?? []).length === 0 &&
      (movement.data ?? []).length === 0 ? (
        <EmptyState
          icon={TrendingUp}
          title={t('analytics.intel.emptyTitle')}
          hint={t('analytics.intel.emptyHint')}
        />
      ) : null}
    </div>
  );
}
